// src/features/reports/components/Reports/components/ReportsTableSection.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import styles from '../Reports.module.css';
import { formatNumber } from '../../../../../shared/utils/formatters';
import { TYPE_MAP, STATUS_MAP } from '../hooks/useReportsData';
import BookingCard from './BookingCard';
import Pagination  from './Pagination';

// ── جدول الحجوزات ────────────────────────────────────────────────
function BookingsTable({ rows }) {
  return (
    <div className={styles.tWrap}>
      <table className={styles.tbl}>
        <thead>
          <tr>
            <th>العميل</th>
            <th>نوع الحجز</th>
            <th>التاريخ</th>
            <th>الحالة</th>
            <th>الإجمالي</th>
            <th>المدفوع</th>
            <th>المتبقي</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? rows.map(b => {
            const st = STATUS_MAP[b.status] || { label: b.status, cls: 'sPending' };
            return (
              <tr key={b.id}>
                <td data-label="العميل"><strong>{b.client?.name || '—'}</strong></td>
                <td data-label="نوع الحجز">{TYPE_MAP[b.booking_type] || b.booking_type}</td>
                <td data-label="التاريخ">{b.booking_date}</td>
                <td data-label="الحالة">
                  <span className={`${styles.sBadge} ${styles[st.cls]}`}>{st.label}</span>
                </td>
                <td data-label="الإجمالي" className={styles.numCell}>
                  {formatNumber(b.total_price, 0)}
                </td>
                <td data-label="المدفوع" className={`${styles.numCell} ${styles.paid}`}>
                  {formatNumber(b.total_paid, 0)}
                </td>
                <td
                  data-label="المتبقي"
                  className={`${styles.numCell} ${parseFloat(b.remaining_amount) > 0 ? styles.rem : ''}`}
                >
                  {formatNumber(b.remaining_amount, 0)}
                </td>
                <td>
                  <Link to={`/booking/${b.id}`} className={styles.viewLink}>تفاصيل</Link>
                </td>
              </tr>
            );
          }) : (
            <tr>
              <td colSpan="8" className={styles.emptyRow}>لا توجد حجوزات</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── جدول المصاريف ─────────────────────────────────────────────────
function ExpensesTable({ rows, bookingsDetails }) {
  return (
    <div className={styles.tWrap}>
      <table className={styles.tbl}>
        <thead>
          <tr>
            <th>الحساب</th>
            <th>البيان</th>
            <th>الحجز المرتبط</th>
            <th>التاريخ</th>
            <th>المبلغ</th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? rows.map(e => (
            <tr key={e.id}>
              <td data-label="الحساب">{e.category_name}</td>
              <td data-label="البيان">{e.description}</td>
              <td data-label="الحجز">
                {e.booking
                  ? (bookingsDetails?.find(b => b.id === e.booking)?.client?.name || 'حجز')
                  : <span className={styles.genTag}>عام</span>}
              </td>
              <td data-label="التاريخ">{e.expense_date}</td>
              <td data-label="المبلغ" className={`${styles.numCell} ${styles.rem}`}>
                {formatNumber(e.amount, 0)}
              </td>
            </tr>
          )) : (
            <tr>
              <td colSpan="5" className={styles.emptyRow}>لا توجد مصاريف</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── المكوّن الرئيسي ───────────────────────────────────────────────
export default function ReportsTableSection({
  reportData,
  // عرض
  tableView, setTableView,
  displayMode, setDisplayMode,
  expenseFilter, setExpenseFilter,
  isSpecFilter,
  // بيانات
  revRows, revPage, setRevPage,
  expRows, expPage, setExpPage,
  filteredExpenses,
}) {
  if (!reportData) return null;

  return (
    <div className={styles.tableSection}>

      {/* ── رأس القسم ── */}
      <div className={styles.tableSectionHead}>

        {/* تبويبات الحجوزات / المصاريف */}
        <div className={styles.tableTabs}>
          <button
            className={`${styles.tabBtn} ${tableView === 'revenue' ? styles.tabActive : ''}`}
            onClick={() => setTableView('revenue')}
          >
            الحجوزات
            <span className={styles.tabCount}>
              {reportData.bookings_details.length}
            </span>
          </button>
          <button
            className={`${styles.tabBtn} ${tableView === 'expenses' ? styles.tabActive : ''}`}
            onClick={() => setTableView('expenses')}
          >
            المصاريف
            <span className={styles.tabCount}>{filteredExpenses.length}</span>
          </button>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          {/* تبديل جدول / بطاقات — للحجوزات فقط */}
          {tableView === 'revenue' && (
            <div className={styles.viewToggle}>
              <button
                className={`${styles.vtBtn} ${displayMode === 'table' ? styles.vtActive : ''}`}
                onClick={() => setDisplayMode('table')}
                title="جدول"
              >
                ☰
              </button>
              <button
                className={`${styles.vtBtn} ${displayMode === 'cards' ? styles.vtActive : ''}`}
                onClick={() => setDisplayMode('cards')}
                title="بطاقات"
              >
                ⊞
              </button>
            </div>
          )}

          {/* فلتر المصاريف */}
          {tableView === 'expenses' && (
            <div className={styles.expSegment}>
              {['all', 'booking', 'general'].map(k => (
                <button
                  key={k}
                  className={`${styles.segBtn} ${expenseFilter === k ? styles.segActive : ''}`}
                  onClick={() => { setExpenseFilter(k); setExpPage(1); }}
                >
                  {k === 'all' ? 'الكل' : k === 'booking' ? 'حجوزات' : 'عامة'}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── محتوى الحجوزات ── */}
      {tableView === 'revenue' && (
        <>
          {displayMode === 'cards' ? (
            <div className={styles.cardsGrid}>
              {revRows.map(b => <BookingCard key={b.id} booking={b} />)}
            </div>
          ) : (
            <BookingsTable rows={revRows} />
          )}
          <Pagination
            total={reportData.bookings_details.length}
            page={revPage}
            onPage={setRevPage}
          />
        </>
      )}

      {/* ── محتوى المصاريف ── */}
      {tableView === 'expenses' && (
        <>
          {isSpecFilter && expenseFilter === 'general' ? (
            <p className={styles.emptyRow} style={{ padding: '2rem' }}>
              المصاريف العامة مخفية عند فلترة نوع حجز محدد
            </p>
          ) : (
            <ExpensesTable
              rows={expRows}
              bookingsDetails={reportData.bookings_details}
            />
          )}
          <Pagination
            total={filteredExpenses.length}
            page={expPage}
            onPage={setExpPage}
          />
        </>
      )}

    </div>
  );
}